/**
 * Top-bar timeclock (WP4-TIMECLOCK). Shown with `timeclock.use` when one
 * office is selected (entries belong to an office):
 *
 * - out:      "Clock in" (success tone)
 * - in:       elapsed time, a Break button and "Clock out" (danger tone)
 * - on break: break time and "End break", which clocks back in
 * - open over 12 hours: a warning chip; a manager closes it with the real
 *   time from Timesheets (the API refuses a self clock-out then)
 *
 * - "PIN": the PIN pad, so anyone at this screen can punch with their own
 *   PIN whoever is signed in (also on the kiosk page, /timeclock/kiosk)
 * - at a PMS office the punch goes to the PMS time clock when the office has
 *   it turned on: the toast says "Sending to Dentrix...", then a second toast
 *   says "Sent to Dentrix." or "not sent, punch in Dentrix too"; the menu
 *   marks each punch. Otherwise the toast carries the API's "Do this in
 *   <PMS>" reminder, which says why it was not sent.
 *
 * State stays current across tabs and devices: the API publishes `timeclock`
 * on every change (the shell's live events refetch keys starting with it), a
 * BroadcastChannel tells this browser's other tabs at once, and the query
 * refetches on focus and every minute as a fallback.
 *
 * Navigation review P0 (one compact status chip, no new chrome in Dentrix):
 * - `TimeclockChip`: Velora Modern's status chip segment, one button whose
 *   label is the state ("Clock in", "In 3:12", "Break 0:10") and whose menu
 *   holds Break / Clock out / End break;
 * - `TimeclockStatusText`: the elapsed time in the Dentrix status bar;
 * - `TimeclockButton`: the full controls, shown by the File > Time Clock
 *   dialog (TimeclockHost) in the familiar skins.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  OPEN_FLAG_HOURS,
  TIMECLOCK_ENTITY,
  TIMECLOCK_PERMISSIONS,
  SyncedTimeclockStatusSchema as TimeclockStatusSchema,
  type SyncedTimeclockStatus as TimeclockStatus,
} from '@velora/contracts/timeclock';
import { ApiError } from '@velora/sdk';
import { Dialog, Menu, toast, type MenuEntry } from '@velora/ui';
import { Clock, Coffee, KeyRound, LogIn, LogOut, TriangleAlert } from 'lucide-react';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useAction } from '../actions/registry.js';
import { clientFor } from '../api.js';
import { useOfficeScope } from '../office/OfficeScope.js';
import { officeQueryKey, useOfficeQuery } from '../office/useOfficeQuery.js';
import { usePermission } from '../session/session.js';

const PinPad = lazy(() =>
  import('../../modules/timeclock/parts/PinPad.js').then((m) => ({ default: m.PinPad })),
);

/** Same name as the timesheet page's channel (modules/timeclock/api.ts). */
const CHANNEL = `velora:${TIMECLOCK_ENTITY}`;
const STATUS_KEY = [TIMECLOCK_ENTITY, 'status'] as const;

/** Elapsed milliseconds as `h:mm`. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 60_000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function useNow(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [enabled]);
  return now;
}

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } } | null;
    return body?.error?.message ?? fallback;
  }
  return fallback;
}

type Action = 'in' | 'out' | 'break';

const clockTime = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/**
 * The toast after a punch (assistant role test fix 8): the time, and the
 * status it leaves ("You are clocked in", "On break"). Pure.
 */
export function punchToast(
  action: Action,
  endedBreak: boolean,
  where: string,
  next: Pick<TimeclockStatus, 'active' | 'onBreakSince'>,
  now: Date,
): { title: string; status: string } {
  const at = clockTime(now);
  const title =
    action === 'in'
      ? endedBreak
        ? `Break ended at ${at}`
        : `Clocked in at ${where}, ${at}`
      : action === 'break'
        ? `Break started at ${at}`
        : `Clocked out of ${where}, ${at}`;
  const status = next.active
    ? 'Status: on the clock.'
    : next.onBreakSince
      ? 'Status: on break (not counted as worked time).'
      : 'Status: clocked out.';
  return { title, status };
}

type PunchState = 'sending' | 'sent' | 'failed' | null | undefined;

/** " (Dentrix: sent)" after a punch that went to the PMS time clock. Pure. */
function pmsMark(pmsName: string | null, state: PunchState): string {
  if (!pmsName || !state) return '';
  return ` (${pmsName}: ${state === 'sent' ? 'sent' : state === 'sending' ? 'sending' : 'not sent, punch there too'})`;
}

/**
 * Today's punches as menu lines: "In 8:02 AM (Dentrix: sent), out 12:01 PM
 * (break) (Dentrix: sent)". Pure.
 */
export function punchLines(
  today: TimeclockStatus['today'],
  pmsName: string | null = null,
): string[] {
  return (today ?? []).map((e) => {
    // Punched at the PMS time clock (read from it): one mark for the line.
    const fromPms = !!e.source && e.source !== 'velora';
    const inAt = `${clockTime(new Date(e.clockedInAt))}${fromPms ? '' : pmsMark(pmsName, e.pmsIn)}`;
    const tail = fromPms && pmsName ? ` (${pmsName} time clock)` : '';
    if (!e.clockedOutAt) {
      const pending =
        fromPms && e.pmsOut ? pmsMark(pmsName, e.pmsOut).replace(': ', ' clock-out: ') : '';
      return `In ${inAt}, still on the clock${tail}${pending}`;
    }
    const outAt = clockTime(new Date(e.clockedOutAt));
    return `In ${inAt}, out ${outAt}${e.outReason === 'break' ? ' (break)' : ''}${fromPms ? tail : pmsMark(pmsName, e.pmsOut)}`;
  });
}

/** How long the chip keeps checking a punch on its way to the PMS. */
const PMS_PUNCH_WATCH_MS = 3 * 60_000;
const PMS_PUNCH_POLL_MS = 5_000;

/**
 * The toast once a punch sent to the PMS time clock has an outcome: sent, or
 * not sent (do it there too). Null while it is still on its way. Pure.
 */
export function pmsPunchToast(
  p: TimeclockStatus['pmsPunch'],
): { title: string; description?: string; tone: 'success' | 'warning' } | null {
  if (!p || p.state === 'sending') return null;
  if (p.state === 'sent') return { title: p.text, tone: 'success' };
  return { title: `Punch not sent to ${pmsLabel(p.system)}`, description: p.text, tone: 'warning' };
}

const pmsLabel = (s: 'dentrix' | 'opendental' | 'eaglesoft') =>
  s === 'dentrix' ? 'Dentrix' : s === 'opendental' ? 'Open Dental' : 'Eaglesoft';

function useTimeclock() {
  const office = useOfficeScope();
  const single = !office.isAll && office.office ? office.office : undefined;
  const allowed = usePermission(TIMECLOCK_PERMISSIONS.use);
  const enabled = allowed && single !== undefined;
  const qc = useQueryClient();

  // A punch on its way to the PMS time clock: checked every few seconds until
  // the PMS answers (or a few minutes pass), then one toast says how it went.
  const [watching, setWatching] = useState<{ commandId: string; until: number } | null>(null);
  const status = useOfficeQuery({
    queryKey: STATUS_KEY,
    queryFn: (c, signal) =>
      c.request('/timeclock/status', { schema: TimeclockStatusSchema, signal }),
    enabled,
    refetchInterval: watching ? PMS_PUNCH_POLL_MS : 60_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  // Other tabs of this browser.
  useEffect(() => {
    if (!enabled || typeof BroadcastChannel === 'undefined') return;
    let ch: BroadcastChannel | null = null;
    try {
      ch = new BroadcastChannel(CHANNEL);
      ch.onmessage = () => void qc.invalidateQueries({ queryKey: [TIMECLOCK_ENTITY] });
    } catch {
      ch = null;
    }
    return () => ch?.close();
  }, [enabled, qc]);

  useEffect(() => {
    if (!watching) return;
    const p = status.data?.pmsPunch;
    const done = p && p.commandId === watching.commandId ? pmsPunchToast(p) : null;
    if (done) {
      setWatching(null);
      toast(done);
      return;
    }
    const left = watching.until - Date.now();
    if (left <= 0) {
      setWatching(null);
      return;
    }
    const t = setTimeout(
      () => setWatching((w) => (w ? { ...w } : w)),
      Math.min(left, PMS_PUNCH_POLL_MS),
    );
    return () => clearTimeout(t);
  }, [watching, status.data?.pmsPunch]);

  // Whether the punch ends a break (read before the status changes).
  const breakRef = useRef(false);
  breakRef.current = Boolean(status.data?.onBreakSince);
  const mutation = useMutation({
    mutationFn: (action: Action): Promise<TimeclockStatus> =>
      clientFor(office.param).request(
        action === 'in' ? '/timeclock/clock-in' : '/timeclock/clock-out',
        {
          method: 'POST',
          body: action === 'break' ? { break: true } : {},
          schema: TimeclockStatusSchema,
        },
      ),
    onSuccess: (next, action) => {
      qc.setQueryData(officeQueryKey(STATUS_KEY, office.param), next);
      void qc.invalidateQueries({ queryKey: [TIMECLOCK_ENTITY] });
      try {
        if (typeof BroadcastChannel !== 'undefined') {
          const ch = new BroadcastChannel(CHANNEL);
          ch.postMessage({ action, at: Date.now() });
          ch.close();
        }
      } catch {
        // Live events still reach the other tabs.
      }
      const where = single?.shortName ?? 'the office';
      if (next.notice) {
        // Nothing changed (e.g. the PMS time clock already has you in).
        toast({ title: next.notice, tone: 'info' });
        return;
      }
      // What happened, when, and where it leaves you (assistant role test fix 8).
      const p = punchToast(action, breakRef.current, where, next, new Date());
      // Sent to the PMS: say so now, and again once the PMS answers.
      const sending =
        next.pmsPunch && next.pmsPunch.state === 'sending' && !next.reminder ? next.pmsPunch : null;
      toast({
        title: p.title,
        description: [p.status, next.reminder?.text ?? sending?.text].filter(Boolean).join(' '),
        tone: 'success',
      });
      if (sending)
        setWatching({ commandId: sending.commandId, until: Date.now() + PMS_PUNCH_WATCH_MS });
    },
    onError: (err) => {
      // Usually another tab already changed it: show the real state.
      void qc.invalidateQueries({ queryKey: [TIMECLOCK_ENTITY] });
      toast({ title: errorText(err, 'The timeclock did not respond. Try again.'), tone: 'danger' });
    },
  });

  const data = status.data;
  const active = data?.active ?? null;
  const breakSince = data?.onBreakSince ?? null;
  const now = useNow(enabled && (active !== null || breakSince !== null));

  const busy = mutation.isPending || (!data && status.isLoading);
  const state: 'flag' | 'in' | 'break' | 'out' = active
    ? active.openTooLong
      ? 'flag'
      : 'in'
    : breakSince
      ? 'break'
      : 'out';
  return {
    enabled,
    single,
    active,
    breakSince,
    now,
    busy,
    state,
    today: data?.today ?? [],
    pmsName: data?.pms?.name ?? null,
    mutate: mutation.mutate,
  };
}

/** The compact status text: "In 3:12", "Break 0:10", "Clock in", or the 12-hour warning. */
export function timeclockLabel(t: ReturnType<typeof useTimeclock>): string {
  if (t.state === 'flag') return `Open over ${OPEN_FLAG_HOURS} h`;
  if (t.state === 'in' && t.active)
    return `In ${formatElapsed(t.now - Date.parse(t.active.clockedInAt))}`;
  if (t.state === 'break' && t.breakSince)
    return `Break ${formatElapsed(t.now - Date.parse(t.breakSince))}`;
  return 'Clock in';
}

/** Velora Modern status chip segment: one button, actions in its menu. */
export function TimeclockChip() {
  const t = useTimeclock();
  if (!t.enabled) return null;
  const items: MenuEntry[] =
    t.state === 'out'
      ? [
          {
            id: 'in',
            label: t.single ? `Clock in at ${t.single.name}` : 'Clock in',
            onSelect: () => t.mutate('in'),
          },
        ]
      : t.state === 'break'
        ? [{ id: 'in', label: 'End break', onSelect: () => t.mutate('in') }]
        : t.state === 'in'
          ? [
              { id: 'break', label: 'Start a break', onSelect: () => t.mutate('break') },
              { id: 'out', label: 'Clock out', tone: 'danger', onSelect: () => t.mutate('out') },
            ]
          : [
              {
                id: 'flag',
                label: 'Ask a manager to close it from Timesheets',
                disabled: true,
                onSelect: () => undefined,
              },
            ];
  const lines = punchLines(t.today, t.pmsName);
  const withToday: MenuEntry[] = lines.length
    ? [
        ...items,
        { id: 'today-sep', type: 'separator' },
        ...lines.map<MenuEntry>((l, i) => ({
          id: `today-${i}`,
          label: l,
          disabled: true,
          onSelect: () => undefined,
        })),
      ]
    : items;
  return (
    <Menu
      align="end"
      items={withToday}
      trigger={
        <button
          type="button"
          className="shell-timeclock__chip"
          data-state={t.state}
          disabled={t.busy}
          aria-label={`Timeclock: ${timeclockLabel(t)}`}
        >
          {t.state === 'flag' ? (
            <TriangleAlert size={14} aria-hidden />
          ) : t.state === 'break' ? (
            <Coffee size={14} aria-hidden />
          ) : t.state === 'in' ? (
            <span className="shell-timeclock__dot" aria-hidden />
          ) : (
            <Clock size={14} aria-hidden />
          )}
          <span className="shell-timeclock__elapsed">{timeclockLabel(t)}</span>
        </button>
      }
    />
  );
}

/** The Dentrix status bar's timeclock cell: "Clocked in 3:12" or "On break 0:10"; nothing when out. */
export function TimeclockStatusText() {
  const t = useTimeclock();
  if (!t.enabled || t.state === 'out') return null;
  return (
    <span data-testid="status-timeclock">
      {t.state === 'break'
        ? `On ${timeclockLabel(t).toLowerCase()}`
        : t.state === 'in'
          ? `Clocked ${timeclockLabel(t).toLowerCase()}`
          : timeclockLabel(t)}
    </span>
  );
}

/**
 * File > Time Clock in the familiar skins (Dentrix adds no toolbar or
 * menu-bar buttons): the action `timeclock.open` shows the full controls.
 */
export function TimeclockHost() {
  const [open, setOpen] = useState(false);
  const allowed = usePermission(TIMECLOCK_PERMISSIONS.use);
  useAction('timeclock.open', () => setOpen(true), {
    label: 'Time Clock',
    icon: 'clock',
    enabled: allowed,
    ...(allowed ? {} : { disabledReason: 'Clocking in needs the timeclock permission.' }),
  });
  if (!open) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && setOpen(false)} title="Time Clock">
      <TimeclockButton />
      <p className="shell-timeclock__hint">
        Pick one office in the office selector to clock in there. Managers fix hours from
        Timesheets.
      </p>
    </Dialog>
  );
}

export function TimeclockButton() {
  const { enabled, single, active, breakSince, now, busy, state, mutate } = useTimeclock();
  const [pad, setPad] = useState(false);
  if (!enabled) return null;
  const mutation = { mutate };

  const padButton = (
    <button
      type="button"
      className="shell-timeclock__btn shell-timeclock__btn--quiet"
      onClick={() => setPad(true)}
      title="Punch in or out with a PIN (anyone, whoever is signed in)"
    >
      <KeyRound size={14} aria-hidden />
      <span className="shell-timeclock__text">PIN</span>
    </button>
  );

  return (
    <div className="shell-timeclock" data-state={state} aria-live="polite">
      {padButton}
      {pad ? (
        <Dialog
          open
          onOpenChange={(o) => (!o ? setPad(false) : undefined)}
          title="Punch in or out with a PIN"
          description={`At ${single?.name ?? 'this office'}. Anyone can punch here with their own PIN.`}
          size="sm"
        >
          <Suspense fallback={null}>
            <PinPad deviceLabel={`${single?.shortName ?? 'Office'} screen`} />
          </Suspense>
        </Dialog>
      ) : null}
      {state === 'flag' && active ? (
        <span
          className="shell-timeclock__status"
          title={`Clocked in ${formatElapsed(now - Date.parse(active.clockedInAt))} ago. Ask a manager to close it with the time you left.`}
        >
          <TriangleAlert size={14} aria-hidden />
          <span className="shell-timeclock__text">
            Open over {OPEN_FLAG_HOURS} h, ask a manager
          </span>
        </span>
      ) : null}

      {state === 'in' && active ? (
        <>
          <span
            className="shell-timeclock__status"
            title={`Clocked in at ${new Date(active.clockedInAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
          >
            <span className="shell-timeclock__dot" aria-hidden />
            <span className="shell-timeclock__label">In</span>
            <span className="shell-timeclock__elapsed">
              {formatElapsed(now - Date.parse(active.clockedInAt))}
            </span>
          </span>
          <button
            type="button"
            className="shell-timeclock__btn shell-timeclock__btn--quiet"
            onClick={() => mutation.mutate('break')}
            disabled={busy}
            title="Start a break or lunch (clocks you out until you end it)"
          >
            <Coffee size={14} aria-hidden />
            <span className="shell-timeclock__text">Break</span>
          </button>
          <button
            type="button"
            className="shell-timeclock__btn shell-timeclock__btn--out"
            onClick={() => mutation.mutate('out')}
            disabled={busy}
          >
            <LogOut size={14} aria-hidden />
            <span>Clock out</span>
          </button>
        </>
      ) : null}

      {state === 'break' && breakSince ? (
        <>
          <span className="shell-timeclock__status" title="On break: not counted as worked time">
            <Coffee size={14} aria-hidden />
            <span className="shell-timeclock__label">Break</span>
            <span className="shell-timeclock__elapsed">
              {formatElapsed(now - Date.parse(breakSince))}
            </span>
          </span>
          <button
            type="button"
            className="shell-timeclock__btn shell-timeclock__btn--in"
            onClick={() => mutation.mutate('in')}
            disabled={busy}
          >
            <LogIn size={14} aria-hidden />
            <span>End break</span>
          </button>
        </>
      ) : null}

      {state === 'out' ? (
        <button
          type="button"
          className="shell-timeclock__btn shell-timeclock__btn--in"
          onClick={() => mutation.mutate('in')}
          disabled={busy}
          title={single ? `Clock in at ${single.name}` : undefined}
        >
          <LogIn size={14} aria-hidden />
          <span>Clock in</span>
        </button>
      ) : null}
    </div>
  );
}
